import React, { useState } from 'react';
import { 
  ListFilter, 
  ChevronDown,
  MapPin,
  Camera,
  Flower2,
  UtensilsCrossed,
  Shirt,
  Music,
  Mail,
  Heart,
  CalendarDays
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Category icon and color mapping using modern Lucide icons
const getCategoryConfig = (categoryId: string) => {
  const configMap: Record<string, {
    icon: React.ComponentType<any>;
    bgGradient: string;
    iconColor: string;
    hoverIconColor: string;
  }> = {
    venue: {
      icon: MapPin,
      bgGradient: 'from-purple-100 to-violet-100',
      iconColor: 'text-purple-600',
      hoverIconColor: 'group-hover:text-purple-700'
    },
    photo: {
      icon: Camera,
      bgGradient: 'from-emerald-100 to-green-100',
      iconColor: 'text-emerald-600',
      hoverIconColor: 'group-hover:text-emerald-700'
    },
    flowers: {
      icon: Flower2,
      bgGradient: 'from-pink-100 to-rose-100',
      iconColor: 'text-pink-600',
      hoverIconColor: 'group-hover:text-pink-700'
    },
    food: {
      icon: UtensilsCrossed,
      bgGradient: 'from-orange-100 to-amber-100',
      iconColor: 'text-orange-600',
      hoverIconColor: 'group-hover:text-orange-700'
    },
    attire: {
      icon: Shirt,
      bgGradient: 'from-indigo-100 to-blue-100',
      iconColor: 'text-indigo-600',
      hoverIconColor: 'group-hover:text-indigo-700'
    },
    music: {
      icon: Music,
      bgGradient: 'from-red-100 to-crimson-100',
      iconColor: 'text-red-600',
      hoverIconColor: 'group-hover:text-red-700'
    },
    invitations: {
      icon: Mail,
      bgGradient: 'from-teal-100 to-cyan-100',
      iconColor: 'text-teal-600',
      hoverIconColor: 'group-hover:text-teal-700'
    },
    honeymoon: {
      icon: Heart,
      bgGradient: 'from-rose-100 to-pink-100',
      iconColor: 'text-rose-600',
      hoverIconColor: 'group-hover:text-rose-700'
    },
    default: {
      icon: CalendarDays,
      bgGradient: 'from-gray-100 to-slate-100',
      iconColor: 'text-gray-600',
      hoverIconColor: 'group-hover:text-gray-700'
    }
  };
  
  return configMap[categoryId] || configMap.default;
};

interface Category {
  id: string;
  title: string;
  icon: string;
}

interface CategorySelectorProps {
  categories: Category[];
  selectedCategory: string;
  onSelectCategory: (categoryId: string) => void;
}

export const CategorySelector: React.FC<CategorySelectorProps> = ({
  categories,
  selectedCategory,
  onSelectCategory,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedCategoryData = categories.find(cat => cat.id === selectedCategory);
  const selectedConfig = selectedCategoryData ? getCategoryConfig(selectedCategoryData.id) : getCategoryConfig('default');

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center px-3 py-2 sm:px-4 sm:py-2.5 bg-white rounded-lg shadow-sm 
          border border-[#E6A5B5] hover:bg-[#FFF7FA] transition-all duration-300 
          text-xs sm:text-sm"
      >
        <ListFilter className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2 text-[#6E497E]" />
        <span className="text-[#6E497E] flex items-center gap-2">
          {selectedCategoryData && React.createElement(selectedConfig.icon, {
            className: `w-4 h-4 ${selectedConfig.iconColor}`
          })}
          {selectedCategoryData?.title}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ml-2 transition-transform text-[#6E497E] ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="absolute left-0 top-full mt-1 bg-white rounded-lg shadow-lg 
              border border-[#E6A5B5] py-1.5 w-48 sm:w-56 z-50"
          >
            {categories.map(category => {
              const config = getCategoryConfig(category.id);
              return (
                <button
                  key={category.id}
                  onClick={() => {
                    onSelectCategory(category.id);
                    setIsOpen(false);
                  }}
                  className={`w-full px-3 py-2 sm:px-4 sm:py-2.5 text-left flex items-center gap-2 
                    transition-all duration-200 text-xs sm:text-sm
                    hover:bg-[#FFF7FA] group
                    ${selectedCategory === category.id 
                      ? 'bg-[#FFF7FA]' 
                      : ''
                    }`}
                >
                  {React.createElement(config.icon, {
                    className: `w-4 h-4 ${config.iconColor} ${config.hoverIconColor} transition-colors`
                  })}
                  <span className={`transition-colors duration-200
                    ${selectedCategory === category.id 
                      ? 'text-[#6E497E] font-medium' 
                      : 'text-gray-700 group-hover:text-[#6E497E]'
                    }`}>
                    {category.title}
                  </span>
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};